import {
  Box,
  Typography,
  Divider,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import useValueExpressions from "../hooks/use-value-expressions";
import useRegexManagement from "./use-regex-management";
import RegexList from "./regex-list";
import RegexFormDialog from "./regex-form-dialog";
import DeleteConfirmDialog from "./delete-confirm-dialog";

interface TemplateValidRegexProps {
  templateId: number;
}

const TemplateValidRegex = ({ templateId }: TemplateValidRegexProps) => {
  const { valueExpressions, isLoading, mutate } =
    useValueExpressions(templateId);

  const validRegexList = valueExpressions || [];
  const expressionStrings = validRegexList.map((item) => item.expression);

  const {
    openDialog,
    newRegex,
    setNewRegex,
    editIndex,
    testValue,
    setTestValue,
    testResult,
    setTestResult,
    isTestSuccessful,
    setIsTestSuccessful,
    isTesting,
    isSaving,
    saveError,
    deleteConfirmOpen,
    indexToDelete,
    isDeleting,
    handleOpenDialog,
    handleOpenEditDialog,
    handleCloseDialog,
    handleTestExpression,
    handleSaveRegex,
    handleRemoveRegex,
    handleConfirmDelete,
    handleCancelDelete,
  } = useRegexManagement({ templateId, validRegexList, mutate });

  const handleResetTest = () => {
    setTestResult(null);
    setIsTestSuccessful(false);
  };

  return (
    <Box>
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 3,
            mb: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="body2" fontWeight={600}>
              Valid Regular Expression
            </Typography>
            {isLoading ? (
              <CircularProgress size={16} />
            ) : (
              <Chip
                label={`${expressionStrings.length} active`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            )}
          </Box>
          <IconButton
            size="small"
            color="success"
            onClick={handleOpenDialog}
            aria-label="Add new expression"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 1 }} />

        <Box
          sx={{
            border: 1,
            borderColor: "grey.300",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <RegexList
            expressions={expressionStrings}
            onEdit={(index) =>
              handleOpenEditDialog(index, validRegexList[index])
            }
            onDelete={handleRemoveRegex}
          />
        </Box>
      </Box>

      <RegexFormDialog
        open={openDialog}
        isEditMode={editIndex !== null}
        expression={newRegex}
        testValue={testValue}
        testResult={testResult}
        isTestSuccessful={isTestSuccessful}
        isTesting={isTesting}
        isSaving={isSaving}
        saveError={saveError}
        onClose={handleCloseDialog}
        onExpressionChange={setNewRegex}
        onTestValueChange={setTestValue}
        onTest={handleTestExpression}
        onSave={handleSaveRegex}
        onResetTest={handleResetTest}
      />

      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        expression={
          indexToDelete === null
            ? null
            : validRegexList[indexToDelete].expression
        }
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Box>
  );
};

export default TemplateValidRegex;
